'use client';

import { useEffect, useMemo, useState } from 'react';

import type { UserSearchResult } from '@/lib/api';
import { API_URL } from '@/lib/consts';

/** Delays `/user/search` requests long enough to coalesce rapid typing. */
const USER_SEARCH_DEBOUNCE_MILLISECONDS = 300;

/** Defines the caller-controlled behavior for shared user search. */
export interface UseUserSearchParams {
  /** Supplies the raw search string to debounce and submit. */
  query: string;
  /** Excludes a specific username, typically the authenticated user. */
  excludeUsername?: string;
}

/** Describes the shared state exposed by the debounced user search hook. */
export interface UseUserSearchResult {
  /** Returns the latest successful search results after optional self-exclusion. */
  results: UserSearchResult[];
  /** Indicates that a debounced search request is currently in flight. */
  isLoading: boolean;
  /** Reports whether the latest completed request failed. */
  error: boolean;
  /** Tracks whether at least one non-empty search has completed. */
  hasSearched: boolean;
}

/**
 * Shares the debounced `/user/search` flow across authenticated people-search surfaces.
 *
 * The hook trims the query, aborts stale requests, and optionally filters out a
 * caller-provided username so components can suppress self-results without
 * duplicating fetch lifecycle logic.
 */
export function useUserSearch({
  query,
  excludeUsername,
}: UseUserSearchParams): UseUserSearchResult {
  const [rawResults, setRawResults] = useState<UserSearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const normalizedQuery = query.trim();

  useEffect(() => {
    const controller = new AbortController();

    if (!normalizedQuery) {
      setRawResults([]);
      setIsLoading(false);
      setError(false);
      setHasSearched(false);

      return () => {
        controller.abort();
      };
    }

    setRawResults([]);
    setIsLoading(true);
    setError(false);
    setHasSearched(false);

    const timeoutId = window.setTimeout(() => {
      const fetchUsers = async () => {
        try {
          const response = await fetch(
            `${API_URL}/user/search?prefix=${encodeURIComponent(normalizedQuery)}`,
            {
              credentials: 'include',
              signal: controller.signal,
            }
          );

          if (!response.ok) {
            throw new Error('User search failed');
          }

          const data: UserSearchResult[] = await response.json();
          setRawResults(data);
        } catch (searchError) {
          if (searchError instanceof Error && searchError.name === 'AbortError') {
            return;
          }

          console.error('Error searching users:', searchError);
          setRawResults([]);
          setError(true);
        } finally {
          if (!controller.signal.aborted) {
            setHasSearched(true);
            setIsLoading(false);
          }
        }
      };

      void fetchUsers();
    }, USER_SEARCH_DEBOUNCE_MILLISECONDS);

    return () => {
      window.clearTimeout(timeoutId);
      controller.abort();
    };
  }, [normalizedQuery]);

  const results = useMemo(() => {
    if (!excludeUsername) {
      return rawResults;
    }

    return rawResults.filter((result) => result.username !== excludeUsername);
  }, [excludeUsername, rawResults]);

  return { results, isLoading, error, hasSearched };
}
